import FormElement from '@app/components/ui/FormElement';
import { LIST_TABLE_APPEARANCE } from '@app/common/listTableAppearance';
import useSmartErrorHandling from '@hooks/useSmartErrorHandling';
import { type BaseColumn } from '@konnecta/generic-table';
import { AppDataTable } from '@app/components/common/AppDataTable';
import { useLastListParams } from '@app/features/exports/useLastListParams';
import { App, Button, DatePicker, Modal, Segmented, Space, Tag, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { TFunction } from 'i18next';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { listRentals, returnRental } from '../../api';
import type { ActiveRental, ActiveRentalRow } from '../../types';
import { createdAtColumn } from '@app/components/common/createdAtColumn';

type RentalStatus = 'active' | 'overdue';

const DATE_FORMAT = 'YYYY-MM-DD';

// The URL param name only matters for deep links; the term itself lives in table
// state, so it survives with `enableUrlSync: false` (this is a tab — syncing
// would fight the sibling tabs over the same query string).
const RENTALS_URL_PARAM_CONFIG = { external: { searchTerm: 'search' } } as const;

/** Format a nullable ISO/date string, falling back to a dash. */
const renderDate = (value?: string | null): string => (value ? dayjs(value).format('D MMM YYYY') : '-');

const renderRecipient = (row: ActiveRentalRow, t: TFunction): string => {
  if (!row.recipientType) return '-';
  const who = row.recipientName || (row.recipientId != null ? `#${row.recipientId}` : '');
  const typeLabel = t(`inventory-recipient-type-${row.recipientType}`);
  return who ? `${typeLabel}: ${who}` : typeLabel;
};

export const NOT_EXPORTED_INVENTORY_RENTALS = ['actions'] as const;

export const getRentalsTableColumns = (
  t: TFunction,
  onMarkReturned: (row: ActiveRentalRow) => void
): BaseColumn<ActiveRentalRow>[] => [
  {
    id: 'itemId',
    header: t('inventory-rcol-item'),
    accessor: 'itemId',
    align: 'left',
    width: 260,
    minWidth: 200,
    sortable: false,
    copy: false,
    cellRenderer: (value, row) => <Typography>{row.itemName || `#${value as number}`}</Typography>,
  },
  {
    id: 'quantity',
    header: t('inventory-rcol-quantity'),
    accessor: 'quantity',
    align: 'right',
    width: 110,
    sortable: false,
    copy: false,
    cellRenderer: (value) => (
      <div className="flex w-full justify-end">
        <Typography className="tabular-nums">{value as number}</Typography>
      </div>
    ),
  },
  {
    id: 'recipient',
    header: t('inventory-rcol-recipient'),
    accessor: 'recipientName',
    width: 240,
    minWidth: 180,
    sortable: false,
    copy: false,
    cellRenderer: (_value, row) => <Typography>{renderRecipient(row, t)}</Typography>,
  },
  {
    id: 'rentalStart',
    header: t('inventory-rcol-rental-start'),
    accessor: 'rentalStart',
    width: 150,
    sortable: false,
    copy: false,
    cellRenderer: (value) => <Typography>{renderDate(value as string | null)}</Typography>,
  },
  {
    id: 'rentalExpectedReturn',
    header: t('inventory-rcol-expected-return'),
    accessor: 'rentalExpectedReturn',
    width: 150,
    sortable: false,
    copy: false,
    cellRenderer: (value) => <Typography>{renderDate(value as string | null)}</Typography>,
  },
  {
    id: 'effectiveEnd',
    header: t('inventory-rcol-effective-end'),
    accessor: 'effectiveEnd',
    width: 200,
    sortable: false,
    copy: false,
    cellRenderer: (value, row) => (
      <Space size={[4, 4]}>
        <Typography>{renderDate(value as string | null)}</Typography>
        {row.isOverdue && <Tag color="error">{t('inventory-rental-overdue')}</Tag>}
      </Space>
    ),
  },
  createdAtColumn<ActiveRentalRow>(t),
  {
    id: 'actions',
    header: t('inventory-rcol-actions'),
    accessor: 'rentalOutId',
    align: 'center',
    sticky: 'right',
    // Fits "Σήμανση ως επιστραφέν" as well as "Mark returned".
    width: 210,
    minWidth: 210,
    maxWidth: 210,
    sortable: false,
    filterable: false,
    copy: false,
    cellRenderer: (_value, row) => (
      <Button type="link" size="small" onClick={() => onMarkReturned(row)}>
        {t('inventory-mark-returned')}
      </Button>
    ),
  },
];

const RentalsTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const { handleError } = useSmartErrorHandling();
  const { remember, getParams } = useLastListParams();

  const [status, setStatus] = useState<RentalStatus>('active');
  const [reloadKey, setReloadKey] = useState(0);

  // Controlled "Mark returned" modal state.
  const [returningRow, setReturningRow] = useState<ActiveRentalRow | null>(null);
  const [actualReturn, setActualReturn] = useState<Dayjs | null>(dayjs());
  const [isSaving, setIsSaving] = useState(false);

  const fetchRentals = useCallback(
    async (params: { limit: number; offset: number; query?: string; sort?: string; [key: string]: unknown }) => {
      const { limit, offset, query, sort } = params;
      const response = await listRentals(
        remember({
          status,
          offset,
          limit,
          ...(query ? { query } : {}),
          ...(sort ? { sort } : {}),
        })
      );
      return {
        data: response.data,
        total: response.total || response.data.length,
      };
    },
    [status, remember]
  );

  const openReturnModal = useCallback((row: ActiveRentalRow) => {
    setReturningRow(row);
    setActualReturn(dayjs());
  }, []);

  const tableConfig = useMemo(
    () => ({
      tableId: 'inventory-rentals',
      rowKey: 'id' as const,
      storage: { enableUrlSync: false, enableLocalStorage: false },
      fetchData: fetchRentals,
      transformData: (rental: ActiveRental): ActiveRentalRow => ({
        id: rental.rentalOutId,
        rentalOutId: rental.rentalOutId,
        itemId: rental.itemId,
        itemName: rental.itemName,
        quantity: rental.quantity,
        recipientType: rental.recipientType,
        recipientId: rental.recipientId,
        recipientName: rental.recipientName,
        rentalStart: rental.rentalStart,
        rentalExpectedReturn: rental.rentalExpectedReturn,
        effectiveEnd: rental.effectiveEnd,
        createdAt: rental.createdAt,
        isOverdue:
          status === 'overdue' || (rental.effectiveEnd ? dayjs(rental.effectiveEnd).isBefore(dayjs(), 'day') : false),
      }),
      columns: getRentalsTableColumns(t, openReturnModal),
      // Without this the table renders no search box at all: the input is only
      // mounted when an external `searchTerm` filter is configured.
      urlParamConfig: RENTALS_URL_PARAM_CONFIG,
      searchPlaceholder: t('inventory-rentals-search-placeholder'),
      searchConfig: {
        minSearchLength: 3,
        debounceMs: 300,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language, fetchRentals, status, openReturnModal]
  );

  const handleReturn = async () => {
    if (!returningRow) return;
    try {
      setIsSaving(true);
      await returnRental(returningRow.rentalOutId, {
        actualReturn: actualReturn ? actualReturn.format(DATE_FORMAT) : undefined,
      });
      message.success(t('inventory-return-success'));
      setReturningRow(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      handleError(e as Error, { customMessage: t('inventory-return-failed') });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    // No wrapper element (same as ItemsTab): GenericDataTable sizes itself with
    // `height: 100%`, so it has to stay a direct child of the sized tab pane —
    // an auto-height div in between collapses it back to row height.
    <>
      <AppDataTable
        appearance={LIST_TABLE_APPEARANCE}
        config={tableConfig}
        export={{ list: 'inventory_rentals', getParams, notExported: [...NOT_EXPORTED_INVENTORY_RENTALS] }}
        // `leftContent` is rendered next to the search input in the toolbar row,
        // so the status toggle sits beside the search box rather than above it.
        leftContent={
          <Segmented<RentalStatus>
            value={status}
            onChange={(value) => setStatus(value)}
            options={[
              { label: t('inventory-rentals-status-active'), value: 'active' },
              { label: t('inventory-rentals-status-overdue'), value: 'overdue' },
            ]}
          />
        }
        // A refresh token rather than a `key`: remounting on every status change
        // would throw away the term the user just typed into the search box.
        refreshToken={`${status}-${reloadKey}`}
      />

      <Modal
        open={returningRow !== null}
        title={t('inventory-return-modal-title')}
        onCancel={isSaving ? undefined : () => setReturningRow(null)}
        onOk={handleReturn}
        okText={t('inventory-mark-returned')}
        cancelText={t('common-cancel')}
        confirmLoading={isSaving}
        maskClosable={!isSaving}
        destroyOnHidden
      >
        <div className="pt-2">
          <FormElement label={t('inventory-return-actual-date-label')}>
            <DatePicker
              className="w-full"
              value={actualReturn}
              disabled={isSaving}
              disabledDate={(current) => current && current > dayjs().endOf('day')}
              onChange={(value) => setActualReturn(value)}
            />
          </FormElement>
        </div>
      </Modal>
    </>
  );
};

export default RentalsTab;
